/**
 * Ordinal position-class renumbering for pinned CodeStitch grids.
 *
 * Card grids (`cs-item1…N`) and mosaic galleries (`cs-picture1…N`) bind each
 * item's grid cell to an ORDINAL CLASS, not DOM order — so a bare DOM move is
 * visually inert. After a reorder move, `postMoveNormalize` calls this to
 * renumber the ordinal classes of the moved item's parent to match the new DOM
 * order, so whatever now sits in each DOM slot occupies that slot's pinned cell.
 *
 * No-op (returns the input unchanged) when the group has no ordinal-classed
 * children — uniform, non-pinned grids reorder correctly by DOM move alone.
 *
 * Uses DOMParser (never regex) to parse; the only regex here tests an individual
 * class-name STRING (`cs-item12`), which is not HTML parsing.
 */

import { escapeForAttrSelector } from './editable-utils.js';

const _ordinalReCache = Object.create(null);
function ordinalRe(prefix) {
  if (!_ordinalReCache[prefix]) {
    _ordinalReCache[prefix] = new RegExp('^cs-' + prefix + '(\\d+)$');
  }
  return _ordinalReCache[prefix];
}

/**
 * @param {string} sectionHtml
 * @param {{ anchorEid?: string, parentSelector: string, ordinalPrefix: string, normalizeAll?: boolean }} opts
 *   anchorEid — an eid inside the moved item (used to locate its parent group)
 *   parentSelector — the grid container selector (e.g. '.cs-card-group')
 *   ordinalPrefix — 'item' for cs-itemN, 'picture' for cs-pictureN
 * @returns {string} the section HTML with ordinal classes renumbered (or unchanged)
 */
export function normalizeOrdinalClasses(sectionHtml, opts) {
  const { anchorEid, parentSelector, ordinalPrefix, normalizeAll = false } = opts || {};
  if (!sectionHtml || (!anchorEid && !normalizeAll) || !parentSelector || !ordinalPrefix)
    return sectionHtml;
  if (typeof DOMParser === 'undefined') return sectionHtml;

  const re = ordinalRe(ordinalPrefix);
  const doc = new DOMParser().parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return sectionHtml;

  const groups = [];
  if (normalizeAll) {
    try {
      groups.push(...root.querySelectorAll(parentSelector));
    } catch {
      return sectionHtml;
    }
  } else {
    const anchor = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(anchorEid)}"]`);
    const group = anchor?.closest(parentSelector);
    if (group) groups.push(group);
  }
  if (groups.length === 0) return sectionHtml;

  // Scope to direct children that carry an ordinal class. Normal reorders touch
  // only the moved item's parent; cross-container removal can normalize every
  // source group because the removed anchor no longer exists.
  let changed = false;
  for (const group of groups) {
    const ordinalKids = Array.from(group.children).filter((child) =>
      Array.from(child.classList).some((cls) => re.test(cls))
    );
    ordinalKids.forEach((child, index) => {
      Array.from(child.classList).forEach((cls) => {
        if (re.test(cls)) child.classList.remove(cls);
      });
      child.classList.add(`cs-${ordinalPrefix}${index + 1}`);
      changed = true;
    });
  }

  return changed ? root.innerHTML : sectionHtml;
}
